// =============================================================================
// Smart Parser — Fuzzy matching for natural text (Q-304)
// Pure TypeScript + Fuse.js — no React, no API calls
// =============================================================================

import type Fuse from 'fuse.js';
import { classifyConfidence, type SmartDetection, type SmartMatch } from './fuseConfig';
import type { ParsedActividad } from './semanticParser';
import type { Cliente, Contacto, TipoActividadRef } from '@/types';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SmartParseIndexes {
  clientes: Fuse<Cliente>;
  contactos: Fuse<Contacto>;
  tiposActividad?: Fuse<TipoActividadRef>;
}

export interface SmartParseResult extends SmartDetection {
  isDayHeader?: boolean;
}

// ---------------------------------------------------------------------------
// Stopwords — common Spanish words that must NOT fuzzy-match contacts/clients
// ---------------------------------------------------------------------------

const STOPWORDS = new Set([
  // Articles & determiners
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'del', 'al',
  // Prepositions & conjunctions
  'de', 'en', 'con', 'por', 'para', 'sin', 'que', 'como', 'mas', 'pero',
  'entre', 'sobre', 'hasta', 'desde', 'hacia', 'tras', 'ante', 'bajo',
  // Pronouns & short function words
  'es', 'son', 'hay', 'ser', 'fue', 'han', 'van', 'ver', 'dar', 'ser',
  'ya', 'no', 'si', 'se', 'su', 'sus', 'mi', 'mis', 'nos', 'les',
  'todo', 'toda', 'todos', 'todas', 'este', 'esta', 'esto',
  'ese', 'esa', 'eso', 'otro', 'otra', 'otros', 'otras',
  'muy', 'bien', 'mal', 'solo', 'tan', 'aun', 'asi',
  // Common short words that cause false positives against names
  'art', 'res', 'pro', 'dia', 'mes', 'ano', 'ley', 'fin', 'red',
  'via', 'dos', 'tre', 'sol', 'sal', 'mar', 'pan', 'luz', 'paz',
  // Temporal words that should not fuzzy-match contacts
  'hoy', 'ahora', 'ayer', 'semana', 'semanas', 'meses',
  'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo',
]);

// ---------------------------------------------------------------------------
// Area keyword dictionaries
// ---------------------------------------------------------------------------

const AREA_KEYWORDS: { pattern: RegExp; area: 'ADMINISTRATIVO' | 'PERSONAL' }[] = [
  {
    pattern: /\b(factura|facturaci[oó]n|timbre|timbres|cuenta|cuentas|cobr|pago|pagos|n[oó]mina|impuesto|contab|balance|p[oó]liza|cheque|banco|sat|cfdi|xml|estados?\s+cuenta)\b/i,
    area: 'ADMINISTRATIVO',
  },
  {
    pattern: /\b(gym|gimnasio|m[eé]dico|doctor|dentista|club|personal|casa|familia|cita\s+m[eé]dica|yoga)\b/i,
    area: 'PERSONAL',
  },
];

// ---------------------------------------------------------------------------
// Day-of-week detection
// ---------------------------------------------------------------------------

const WEEKDAYS: Record<string, number> = {
  domingo: 0, lunes: 1, martes: 2, miercoles: 3,
  jueves: 4, viernes: 5, sabado: 6,
};

const DAY_HEADER_RE = /^\s*(lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\s*:?\s*$/i;

// ---------------------------------------------------------------------------
// Time detection: HH:MM at word boundary
// ---------------------------------------------------------------------------

const TIME_RE = /\b(\d{1,2}):(\d{2})\b/;

// ---------------------------------------------------------------------------
// Natural temporal expressions (detected without FEC: prefix)
// Order matters: longer patterns first to avoid partial matches
// ---------------------------------------------------------------------------

interface TemporalMatch {
  fechaAgendada: Date;
  consumedText: string;
}

const TEMPORAL_PATTERNS: {
  pattern: RegExp;
  resolve: (ref: Date, match: RegExpMatchArray) => Date;
}[] = [
  // "pasado mañana" / "pasado manana" — must be before "mañana"
  {
    pattern: /\bpasado\s+ma[ñn]ana\b/i,
    resolve: (ref) => {
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      d.setDate(d.getDate() + 2);
      return d;
    },
  },
  // "próxima semana" / "proxima semana" / "la próxima semana" / "siguiente semana"
  {
    pattern: /\b(?:la\s+)?(?:pr[oó]xima|siguiente)\s+semana\b/i,
    resolve: (ref) => {
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      const currentDay = d.getDay();
      // Next Monday
      const daysUntilMonday = currentDay === 0 ? 1 : 8 - currentDay;
      d.setDate(d.getDate() + daysUntilMonday);
      return d;
    },
  },
  // "próximo mes" / "proximo mes" / "el próximo mes" / "siguiente mes"
  {
    pattern: /\b(?:el\s+)?(?:pr[oó]ximo|siguiente)\s+mes\b/i,
    resolve: (ref) => {
      return new Date(ref.getFullYear(), ref.getMonth() + 1, 1);
    },
  },
  // "próximo <weekday>" / "el próximo lunes" — must be before bare weekday
  {
    pattern: /\b(?:el\s+)?pr[oó]ximo\s+(lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\b/i,
    resolve: (ref, match) => {
      const dayName = normalize(match[1]);
      const targetDay = WEEKDAYS[dayName];
      if (targetDay === undefined) return ref;
      return nextWeekday(ref, targetDay);
    },
  },
  // "en N días" / "en N dias"
  {
    pattern: /\ben\s+(\d{1,3})\s+d[ií]as?\b/i,
    resolve: (ref, match) => {
      const n = parseInt(match[1], 10);
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      d.setDate(d.getDate() + n);
      return d;
    },
  },
  // "en N semanas"
  {
    pattern: /\ben\s+(\d{1,2})\s+semanas?\b/i,
    resolve: (ref, match) => {
      const n = parseInt(match[1], 10);
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      d.setDate(d.getDate() + n * 7);
      return d;
    },
  },
  // "en N meses"
  {
    pattern: /\ben\s+(\d{1,2})\s+mes(?:es)?\b/i,
    resolve: (ref, match) => {
      const n = parseInt(match[1], 10);
      return new Date(ref.getFullYear(), ref.getMonth() + n, ref.getDate());
    },
  },
  // "ahora" / "ya" — today
  {
    pattern: /\b(?:ahora|ya)\b/i,
    resolve: (ref) => new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()),
  },
  // "hoy" — today (without FEC: prefix)
  {
    pattern: /\bhoy\b/i,
    resolve: (ref) => new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()),
  },
  // "mañana" / "manana" — tomorrow (without FEC: prefix)
  {
    pattern: /\bma[ñn]ana\b/i,
    resolve: (ref) => {
      const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      d.setDate(d.getDate() + 1);
      return d;
    },
  },
  // Weekday names in natural text: "el lunes", "martes 07:30", "viernes ir a..."
  // Matches with or without article "el"
  {
    pattern: /\b(?:el\s+)?(lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\b/i,
    resolve: (ref, match) => {
      const dayName = normalize(match[1]);
      const targetDay = WEEKDAYS[dayName];
      if (targetDay === undefined) return ref;
      return nextWeekday(ref, targetDay);
    },
  },
  // Inline date: DD/MM or DD-MM (no year)
  {
    pattern: /\b(\d{1,2})[/-](\d{1,2})\b/,
    resolve: (ref, match) => {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // 0-indexed
      if (month < 0 || month > 11 || day < 1 || day > 31) return ref;
      const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
      let year = ref.getFullYear();
      const candidate = new Date(year, month, day);
      if (candidate < today) year += 1;
      return new Date(year, month, day);
    },
  },
];

function detectTemporalExpression(text: string, ref: Date): TemporalMatch | null {
  for (const { pattern, resolve } of TEMPORAL_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      const resolved = resolve(ref, match);
      // Guard: if resolve returned the ref unchanged (error case), skip
      if (resolved === ref) continue;
      return { fechaAgendada: resolved, consumedText: match[0] };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Core smart parse
// ---------------------------------------------------------------------------

export function smartParse(
  text: string,
  indexes: SmartParseIndexes,
  referenceDate?: Date,
): SmartParseResult {
  const result: SmartParseResult = { consumedTokens: [] };
  const ref = referenceDate ?? new Date();

  // 1. Day header (entire line is a day name)
  const dayHeaderMatch = text.match(DAY_HEADER_RE);
  if (dayHeaderMatch) {
    const dayName = normalize(dayHeaderMatch[1]);
    const targetDay = WEEKDAYS[dayName];
    if (targetDay !== undefined) {
      result.fechaAgendada = nextWeekday(ref, targetDay);
      result.isDayHeader = true;
      return result;
    }
  }

  // 2. Time detection
  const timeMatch = text.match(TIME_RE);
  if (timeMatch) {
    const hh = parseInt(timeMatch[1], 10);
    const mm = parseInt(timeMatch[2], 10);
    if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
      result.horaAgendada = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      result.consumedTokens.push(timeMatch[0]);
    }
  }

  // 2b. Natural temporal expressions (ahora, mañana, próxima semana, etc.)
  const temporalMatch = detectTemporalExpression(text, ref);
  if (temporalMatch) {
    result.fechaAgendada = temporalMatch.fechaAgendada;
    result.consumedTokens.push(temporalMatch.consumedText);
  }

  // 3. Client fuzzy match
  const tokens = tokenize(text);
  const clientMatch = findBestMatch(tokens, indexes.clientes);
  if (clientMatch) {
    result.clienteRef = clientMatch.item.nombre;
    result.clienteId = clientMatch.item.id;
    result.clienteMatch = clientMatch;
    result.consumedTokens.push(clientMatch.matchedToken);
  }

  // 4. Contact fuzzy match (try up to trigrams for multi-word names)
  const contactMatch = findBestMatch(tokens, indexes.contactos, 3);
  if (contactMatch) {
    result.contactoRef = contactMatch.item.nombre;
    result.contactoId = contactMatch.item.id;
    result.contactoMatch = contactMatch;
    result.consumedTokens.push(contactMatch.matchedToken);
    // Contact → client link is more reliable than a fuzzy client match.
    // Override the client UNLESS the direct client match has a better (lower) score.
    if (contactMatch.item.cliente_id) {
      const contactClientWins =
        !result.clienteId ||
        !clientMatch ||
        contactMatch.score <= clientMatch.score;
      if (contactClientWins) {
        result.clienteId = contactMatch.item.cliente_id;
        // Clear the fuzzy client ref since contact's client takes over
        if (clientMatch && contactMatch.score <= clientMatch.score) {
          result.clienteRef = undefined;
          result.clienteMatch = undefined;
        }
      }
    }
  }

  // 5. Tipo de actividad fuzzy match (matches against nombre, codigo, aliases)
  if (indexes.tiposActividad) {
    const tipoMatch = findBestMatch(tokens, indexes.tiposActividad);
    if (tipoMatch) {
      result.tipoActividadId = tipoMatch.item.id;
      result.tipoActividadRef = tipoMatch.item.nombre;
      result.tipoActividadMatch = tipoMatch;
      // Auto-set area from tipo's default if not already set
      if (tipoMatch.item.area_default && !result.area) {
        result.area = tipoMatch.item.area_default;
      }
    }
  }

  // 6. Area inference from keywords (only if not already set by tipo de actividad)
  if (!result.area) {
    for (const { pattern, area } of AREA_KEYWORDS) {
      if (pattern.test(text)) {
        result.area = area;
        break;
      }
    }
  }

  // 7. If a client was detected and no area set, default to TRABAJO
  if (result.clienteId && !result.area) {
    result.area = 'TRABAJO';
  }

  return result;
}

// ---------------------------------------------------------------------------
// Merge: smart fills empty fields from semantic parse
// ---------------------------------------------------------------------------

export function mergeSmartIntoSemantic(
  semantic: ParsedActividad,
  smart: SmartParseResult,
): ParsedActividad {
  return {
    ...semantic,
    clienteRef: semantic.clienteRef ?? smart.clienteRef,
    contactoRef: semantic.contactoRef ?? smart.contactoRef,
    tipoActividadCodigo: semantic.tipoActividadCodigo ?? smart.tipoActividadRef,
    horaAgendada: semantic.horaAgendada ?? smart.horaAgendada,
    fechaAgendada: semantic.fechaAgendada ?? smart.fechaAgendada,
    area: semantic.area ?? smart.area,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function normalize(s: string): string {
  return s.toLowerCase()
    .replace(/[áà]/g, 'a')
    .replace(/[éè]/g, 'e')
    .replace(/[íì]/g, 'i')
    .replace(/[óò]/g, 'o')
    .replace(/[úù]/g, 'u');
}

function nextWeekday(ref: Date, targetDay: number): Date {
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const currentDay = today.getDay();
  let diff = targetDay - currentDay;
  if (diff <= 0) diff += 7;
  const d = new Date(today);
  d.setDate(d.getDate() + diff);
  return d;
}

function tokenize(text: string): string[] {
  return text
    .split(/[\s\-–—,;]+/)
    .map((t) => t.replace(/^[.:]+|[.:]+$/g, '').trim())
    .filter((t) => t.length >= 2);
}

interface MatchWithToken<T = unknown> extends SmartMatch<T> {
  matchedToken: string;
}

function findBestMatch<T>(
  tokens: string[],
  fuse: Fuse<T>,
  maxGram: number = 2,
): MatchWithToken<T> | null {
  let best: MatchWithToken<T> | null = null;

  for (let n = maxGram; n >= 1; n--) {
    for (let i = 0; i <= tokens.length - n; i++) {
      const phrase = tokens.slice(i, i + n).join(' ');
      if (phrase.length < 3) continue;

      // Skip stopwords on single-token (unigram) searches to prevent
      // "las" → "Nicolas", "art" → "Martinez" false positives
      if (n === 1 && STOPWORDS.has(phrase.toLowerCase())) continue;

      const results = fuse.search(phrase, { limit: 1 });
      if (results.length === 0) continue;

      const { item, score = 1 } = results[0];
      const confidence = classifyConfidence(score);
      if (confidence === 'none') continue;

      if (!best || score < best.score) {
        best = { item, score, confidence, matchedToken: phrase };
      }
    }
  }

  return best;
}
